import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import manualPaymentVerificationService from '../../services/manualPaymentVerificationService';
import PendingVerificationDashboard from './components/PendingVerificationDashboard';
import PaymentVerificationTable from './components/PaymentVerificationTable';
import AccountActivationWorkflow from './components/AccountActivationWorkflow';
import AccessManagementPanel from './components/AccessManagementPanel';
import AuditLogViewer from './components/AuditLogViewer';
import RealtimePaymentAlerts from './components/RealtimePaymentAlerts';

const ManualPaymentVerificationAccountActivationCenter = () => {
  const [activeTab, setActiveTab] = useState('payment-verification');
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStatistics();
  }, [refreshTrigger]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      const stats = await manualPaymentVerificationService?.getVerificationStatistics();
      setStatistics(stats);
    } catch (err) {
      setError(err?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleNewPayment = (payment) => {
    // Refresh statistics when new payment detected
    handleRefresh();
  };

  const tabs = [
    {
      id: 'payment-verification',
      label: 'Payment Verification',
      icon: 'CreditCard',
      badge: statistics?.pendingPayments || 0
    },
    {
      id: 'account-activation',
      label: 'Account Activation',
      icon: 'UserCheck',
      badge: statistics?.pendingActivations || 0
    },
    {
      id: 'access-management',
      label: 'Access Management',
      icon: 'Shield',
      badge: null
    },
    {
      id: 'audit-log',
      label: 'Audit Log',
      icon: 'FileText',
      badge: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Manual Payment Verification & Account Activation Center
            </h1>
            <p className="text-muted-foreground">
              Verify external bank payments, activate client accounts, and grant portal access
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <Button>
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Pending Payments</span>
              <Icon name="Clock" className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-3xl font-bold text-foreground">{statistics?.pendingPayments || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              K{(statistics?.totalPendingAmountKina || 0)?.toLocaleString()} total
            </p>
          </div>

          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Activation Requests</span>
              <Icon name="UserPlus" className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-foreground">{statistics?.pendingActivations || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Awaiting activation</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Today's Verifications</span>
              <Icon name="CheckCircle" className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-foreground">{statistics?.todayVerifications || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Completed today</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Manual Overrides</span>
              <Icon name="AlertTriangle" className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-foreground">0</p>
            <p className="text-xs text-muted-foreground mt-1">Requires attention</p>
          </div>
        </div>

        {/* Pending Verification Dashboard (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-4">
            <div className="space-y-6">
              <RealtimePaymentAlerts onNewPayment={handleNewPayment} />
              <PendingVerificationDashboard refreshTrigger={refreshTrigger} />
            </div>
          </div>

          {/* Main Verification Panel (8 cols) */}
          <div className="lg:col-span-8">
            <div className="bg-card rounded-lg shadow-sm border border-border">
              {/* Tabs */}
              <div className="border-b border-border">
                <div className="flex overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                      {tab?.badge !== null && tab?.badge > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-error text-white text-xs font-bold rounded-full">
                          {tab?.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'payment-verification' && (
                  <PaymentVerificationTable onVerificationComplete={handleRefresh} />
                )}
                {activeTab === 'account-activation' && (
                  <AccountActivationWorkflow onActivationComplete={handleRefresh} />
                )}
                {activeTab === 'access-management' && (
                  <AccessManagementPanel />
                )}
                {activeTab === 'audit-log' && (
                  <AuditLogViewer />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManualPaymentVerificationAccountActivationCenter;